import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import { ArrowOutward as OutIcon } from '@mui/icons-material';

const LOGO = `${process.env.PUBLIC_URL}/scribelive-logo.png`;
const MARK = `${process.env.PUBLIC_URL}/favicon-192.png`;

/**
 * Split-screen frame for the sign-in pages: a brand panel on the left that
 * says what this portal is for, and the form on the right. The brand panel
 * drops away below `md`, so phones get the form alone.
 *
 * Kept identical across the three portals; each passes its own palette and copy.
 */
export default function AuthShell({
  portalName,
  panelBackground,
  accent,
  headlineFont,
  eyebrow,
  headline,
  body,
  points = [],
  preview,
  children,
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.1fr) minmax(0, 1fr)' },
        bgcolor: 'background.paper',
      }}
    >
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 5,
          position: 'relative',
          overflow: 'hidden',
          px: { md: 6, lg: 8 },
          py: 6,
          color: '#fff',
          background: panelBackground,
        }}
      >
        {/* Dot grid + two soft glows — the same texture the website hero uses. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.2))',
          }}
        />
        <Box aria-hidden sx={{ position: 'absolute', top: -160, right: -120, width: 420, height: 420, borderRadius: '50%', bgcolor: accent, opacity: 0.22, filter: 'blur(90px)', pointerEvents: 'none' }} />
        <Box aria-hidden sx={{ position: 'absolute', bottom: -180, left: -140, width: 380, height: 380, borderRadius: '50%', bgcolor: accent, opacity: 0.14, filter: 'blur(90px)', pointerEvents: 'none' }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 6px 20px rgba(0,0,0,0.18)' }}>
            <Box component="img" src={MARK} alt="" sx={{ width: 30, height: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, letterSpacing: 0.2 }}>ScribeLive</Typography>
          <Box sx={{ ml: 0.5, px: 1.25, py: 0.25, borderRadius: 99, border: '1px solid rgba(255,255,255,0.28)', fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
            {portalName}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', maxWidth: 520 }}>
          {eyebrow && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2.5, px: 1.5, py: 0.5, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', fontSize: 12.5, fontWeight: 600, letterSpacing: 0.3 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: accent, boxShadow: `0 0 0 4px rgba(255,255,255,0.08)` }} />
              {eyebrow}
            </Box>
          )}
          <Typography
            component="h1"
            sx={{ fontFamily: headlineFont, fontWeight: 700, fontSize: { md: 38, lg: 46 }, lineHeight: 1.08, letterSpacing: '-0.01em', mb: 2 }}
          >
            {headline}
          </Typography>
          {body && (
            <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 16, lineHeight: 1.65, mb: 4 }}>
              {body}
            </Typography>
          )}
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2 }}>
            {points.map(({ icon, title, text }) => (
              <Box component="li" key={title} sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
                <Box sx={{ flexShrink: 0, width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', color: accent, '& svg': { fontSize: 19 } }}>
                  {icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{title}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.68)', fontSize: 13.5, lineHeight: 1.5 }}>{text}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ position: 'relative' }}>{preview}</Box>
      </Box>

      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          px: { xs: 2.5, sm: 6, lg: 10 },
          py: { xs: 3, sm: 5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box component="img" src={LOGO} alt="ScribeLive" sx={{ height: { xs: 30, sm: 36 }, width: 'auto' }} />
          <Link
            href="https://scribelive.us"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13.5, fontWeight: 600, color: 'text.secondary' }}
          >
            scribelive.us <OutIcon sx={{ fontSize: 15 }} />
          </Link>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', py: { xs: 4, sm: 6 } }}>
          <Box sx={{ width: '100%', maxWidth: 400 }}>{children}</Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
          © {new Date().getFullYear()} ScribeLive · Encrypted in transit
        </Typography>
      </Box>
    </Box>
  );
}
